import { type FC, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'src/utils/axios';
import { messageForHouseholdMemberError } from 'src/utils/errors/contactErrorMessages';
import { formatPhoneNumber } from 'src/utils/formatNumber';
import type { AddUserHouseholdMemberPayload } from 'src/types/Certification';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Use the same email validation as the login form
const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AddUserHouseholdMemberScreenProps {
  userHouseholdId: number;
  onSubmit: () => void;
  onCancel: () => void;
}

interface Language {
  id: number;
  name: string;
}

interface FormValues {
  first_name: string;
  last_name: string;
  middle_name: string;
  household_role: 'co_hoh' | 'spouse' | 'member' | 'helper';
  preferred_language: Language | null;
  email: string;
  phone_number: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
}

interface TouchedFields {
  first_name?: boolean;
  last_name?: boolean;
  email?: boolean;
  phone_number?: boolean;
}

// ---------------------------------------------------------------------------
// Shared card style (mirrors accountHubPage cardSx)
// ---------------------------------------------------------------------------

const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AddUserHouseholdMemberScreen: FC<AddUserHouseholdMemberScreenProps> = ({
  userHouseholdId,
  onSubmit,
  onCancel,
}) => {
  const [values, setValues] = useState<FormValues>({
    first_name: '',
    last_name: '',
    middle_name: '',
    household_role: 'member',
    preferred_language: null,
    email: '',
    phone_number: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  const [languages, setLanguages] = useState<Language[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLanguages = async (): Promise<void> => {
      try {
        const res = await axios.get('/household/languages/');
        if (res.status === 200) {
          setLanguages(res.data);
        }
      } catch {
        // non-fatal — language picker remains empty
      }
    };
    void fetchLanguages();
  }, []);

  const validateField = (
    fieldName: keyof FormValues,
    value: string | Language | null
  ): string | undefined => {
    switch (fieldName) {
      case 'first_name':
        if (!value || (typeof value === 'string' && !value.trim())) {
          return 'First Name is required.';
        }
        break;
      case 'last_name':
        if (!value || (typeof value === 'string' && !value.trim())) {
          return 'Last Name is required.';
        }
        break;
      case 'email':
        if (value && typeof value === 'string') {
          if (!emailRegex.test(value)) {
            return 'A valid email address is required.';
          }
        }
        break;
      case 'phone_number':
        // No format validation — phone is live-formatted on keystroke.
        break;
    }
    return undefined;
  };

  const handleFieldChange = (
    fieldName: keyof FormValues,
    value: string | Language | null
  ): void => {
    setValues((prev) => ({
      ...prev,
      [fieldName]: value as FormValues[typeof fieldName],
    }));

    if (touched[fieldName as keyof TouchedFields]) {
      const error = validateField(fieldName, value);
      setErrors((prev) => ({ ...prev, [fieldName]: error }));
    }
  };

  const handleFieldBlur = (fieldName: keyof TouchedFields): void => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    const value = values[fieldName as keyof FormValues] as
      | string
      | Language
      | null;
    const error = validateField(fieldName, value);
    setErrors((prev) => ({ ...prev, [fieldName]: error }));
  };

  const validateAll = (): boolean => {
    const newErrors: FormErrors = {};

    if (!values.first_name.trim()) {
      newErrors.first_name = 'First Name is required.';
    }
    if (!values.last_name.trim()) {
      newErrors.last_name = 'Last Name is required.';
    }
    if (values.email.trim() && !emailRegex.test(values.email.trim())) {
      newErrors.email = 'A valid email address is required.';
    }

    setErrors(newErrors);
    setTouched((prev) => ({
      ...prev,
      first_name: true,
      last_name: true,
      email: true,
      phone_number: true,
    }));

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateAll()) {
      return;
    }
    setSubmitError(null);
    setLoading(true);
    try {
      const payload: AddUserHouseholdMemberPayload = {
        user_household_id: userHouseholdId,
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        household_role: values.household_role,
      };
      if (values.middle_name.trim()) {
        payload.middle_name = values.middle_name.trim();
      }
      if (values.email.trim()) {
        payload.email = values.email.trim();
      }
      if (values.phone_number.trim()) {
        payload.phone_number = values.phone_number.trim();
      }
      if (values.preferred_language) {
        payload.preferred_language = values.preferred_language.id;
      }
      await axios.post('v2/renter-hub/me/my-household/members/', payload);
      onSubmit();
    } catch (err: unknown) {
      setSubmitError(
        messageForHouseholdMemberError(
          err as { detail?: string; error_code?: string; status?: number },
          'Failed to add member. Please try again.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ ...cardSx, p: 3 }}>
      <Stack gap={2.5}>
        <Typography
          sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}
        >
          Add New Member
        </Typography>

        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {/* Required fields */}
        <TextField
          label="First Name"
          required
          fullWidth
          value={values.first_name}
          onChange={(e) => handleFieldChange('first_name', e.target.value)}
          onBlur={() => handleFieldBlur('first_name')}
          error={touched.first_name && Boolean(errors.first_name)}
          helperText={touched.first_name && errors.first_name}
          disabled={loading}
          size="small"
        />

        <TextField
          label="Middle Name"
          fullWidth
          value={values.middle_name}
          onChange={(e) => handleFieldChange('middle_name', e.target.value)}
          disabled={loading}
          size="small"
        />

        <TextField
          label="Last Name"
          required
          fullWidth
          value={values.last_name}
          onChange={(e) => handleFieldChange('last_name', e.target.value)}
          onBlur={() => handleFieldBlur('last_name')}
          error={touched.last_name && Boolean(errors.last_name)}
          helperText={touched.last_name && errors.last_name}
          disabled={loading}
          size="small"
        />

        <FormControl fullWidth size="small">
          <InputLabel id="household-role-label">Role</InputLabel>
          <Select
            labelId="household-role-label"
            label="Role"
            value={values.household_role}
            onChange={(e) =>
              handleFieldChange(
                'household_role',
                e.target.value as FormValues['household_role']
              )
            }
            disabled={loading}
          >
            <MenuItem value="member">Household Member</MenuItem>
            <MenuItem value="helper">Helper</MenuItem>
            <MenuItem value="co_hoh">Co-Head of Household</MenuItem>
            <MenuItem value="spouse">Spouse</MenuItem>
          </Select>
        </FormControl>

        <Autocomplete
          fullWidth
          size="small"
          options={languages}
          getOptionLabel={(option) => option.name}
          value={values.preferred_language}
          onChange={(_event, newValue) =>
            handleFieldChange('preferred_language', newValue)
          }
          disabled={loading}
          renderInput={(params) => (
            <TextField {...params} label="Preferred Language" size="small" />
          )}
        />

        {/* Optional contact fields */}
        <TextField
          label="Email"
          type="email"
          fullWidth
          value={values.email}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          onBlur={() => handleFieldBlur('email')}
          error={touched.email && Boolean(errors.email)}
          helperText={touched.email && errors.email}
          disabled={loading}
          size="small"
        />

        <TextField
          label="Phone Number"
          type="tel"
          placeholder="(555) 123-4567"
          fullWidth
          value={values.phone_number}
          onChange={(e) => {
            const formatted = formatPhoneNumber(e.target.value);
            handleFieldChange('phone_number', formatted);
          }}
          disabled={loading}
          size="small"
        />

        <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
          Recipients can opt out of SMS messages at any time through their
          account settings.
        </Typography>

        {/* Actions */}
        <Stack direction="row" gap={1.5} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => void handleSubmit()}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            <span>{loading ? 'Saving…' : 'Save Member'}</span>
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default AddUserHouseholdMemberScreen;
